/**
 * SyncResultToast Component
 *
 * Displays sync results using sonner toast notifications.
 * Shows imported count, skipped count, unmapped accounts warning, and errors.
 */

import { toast } from 'sonner';
import type { SyncResult } from '@/types/accounting';

/**
 * Show a toast notification with sync results
 */
export function showSyncResultToast(result: SyncResult): void {
  if (result.success) {
    if (result.importedCount === 0 && result.skippedCount === 0) {
      // No new transactions
      toast.info('No new transactions found', {
        description: 'Your data is already up to date.',
      });
    } else if (result.unmappedAccounts.length > 0) {
      // Success with warnings
      toast.warning('Sync completed with warnings', {
        description: (
          <SyncSuccessWithWarnings result={result} />
        ),
        duration: 8000,
      });
    } else {
      // Full success
      toast.success('Sync completed successfully', {
        description: (
          <SyncSuccessMessage result={result} />
        ),
        duration: 5000,
      });
    }
  } else {
    // Error
    const errorMessage = result.errors.length > 0
      ? result.errors.join('. ')
      : 'An unknown error occurred during sync.';

    toast.error('Sync failed', {
      description: errorMessage,
      duration: 8000,
    });
  }
}

/**
 * Success message content
 */
function SyncSuccessMessage({ result }: { result: SyncResult }) {
  return (
    <div className="text-sm">
      <span>
        Imported <strong>{result.importedCount}</strong> new transaction
        {result.importedCount !== 1 ? 's' : ''}
      </span>
      {result.skippedCount > 0 && (
        <span className="text-gray-500">
          {' '} (skipped {result.skippedCount} duplicate{result.skippedCount !== 1 ? 's' : ''})
        </span>
      )}
    </div>
  );
}

/**
 * Success with warnings message content
 */
function SyncSuccessWithWarnings({ result }: { result: SyncResult }) {
  return (
    <div className="text-sm space-y-1">
      <div>
        Imported <strong>{result.importedCount}</strong> transaction
        {result.importedCount !== 1 ? 's' : ''}
        {result.skippedCount > 0 && (
          <span> (skipped {result.skippedCount})</span>
        )}
      </div>
      {result.unmappedAccounts.length > 0 && (
        <div className="text-amber-700">
          {result.unmappedAccounts.length} unmapped account
          {result.unmappedAccounts.length !== 1 ? 's' : ''}:
          <ul className="list-disc list-inside ml-2 mt-0.5">
            {result.unmappedAccounts.slice(0, 3).map((account) => (
              <li key={account} className="font-mono text-xs">
                {account}
              </li>
            ))}
            {result.unmappedAccounts.length > 3 && (
              <li className="text-xs italic">
                ...and {result.unmappedAccounts.length - 3} more
              </li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
